import { z } from 'zod';

/** Reusable field schemas for collection contracts. */

export const id = z.uuid();
export const isoDate = z.iso.date();
export const isoDateTime = z.iso.datetime({ offset: true });
export const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm');

export const shortText = (max = 200) => z.string().trim().max(max);
export const requiredText = (max = 200) => z.string().trim().min(1, 'Required').max(max);
export const longText = (max = 10_000) => z.string().max(max);

export const tagList = z.array(z.string().trim().min(1).max(40)).max(20);

/** Money is always integer minor units (cents) with an ISO 4217 code. Never floats. */
export const currencyCode = z.string().regex(/^[A-Z]{3}$/);
export const cents = z.number().int().min(-1_000_000_000_00).max(1_000_000_000_00);

/** A typed pointer from one record to another (e.g. a document related to a pet). */
export const relatedRef = z.object({
  collection: z.string().regex(/^[a-z_]{1,40}$/),
  id,
});
export type RelatedRef = z.infer<typeof relatedRef>;
export const relatedRefs = z.array(relatedRef).max(20);

/**
 * Where an imported record came from. Imported items become normal
 * app-owned records; provenance is kept for display and idempotent re-import.
 */
export const provenance = z.object({
  provider: z.enum(['google_calendar', 'google_photos', 'google_drive', 'sample_fixture']),
  externalId: z.string().min(1).max(512),
  importedAt: isoDateTime,
  sourceName: z.string().max(300).optional(),
});
export type Provenance = z.infer<typeof provenance>;

export const httpUrl = z
  .string()
  .max(2000)
  .refine((v) => v === '' || /^https?:\/\//i.test(v), 'Must start with http:// or https://');
